import { useMemo } from 'react';

import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import { getLSProductUri } from 'services/generic';
import { filterLargeSizeData } from 'services/product/largeSizeMode';
import { uniqSizeLabel } from 'settings/product';

import {
  FormattedProductDataType,
  ProductColorsType,
  ProductKind,
  ProductStaticDataType,
  ProductType,
  ProductVariantsColorsType,
  ProductVariantsSkusType,
  ProductVariantType,
  SelectedProductAttributes,
} from 'types/ProductPageV2/Products';

export default function useProductStaticFormatted(
  data?: ProductStaticDataType,
  selectedColor?: string,
  forceProductSku?: string
): FormattedProductDataType | undefined {
  const { LSModeEnabled } = useLargeSizeMode();

  function getLessVariantProduct(
    variants: ProductVariantsSkusType
  ): ProductVariantType {
    return Object.values(variants).reduce((lowest, current) => {
      if (parseFloat(current.Price) < parseFloat(lowest.Price)) {
        return current;
      }

      return lowest;
    });
  }

  function formatProductAttributes(
    product: ProductType,
    variants: ProductVariantsColorsType
  ): SelectedProductAttributes {
    const { couleur: color, SKUReelle: productSKUReelle } = product.Attributes;

    const targetColor = selectedColor ?? color.Value;
    const variantSkus = (variants[targetColor] || undefined) as
      | ProductVariantsSkusType
      | undefined;

    const selectedVariantSku = variantSkus
      ? forceProductSku
        ? variantSkus[forceProductSku]
        : getLessVariantProduct(variantSkus)
      : undefined;

    const targetSku =
      selectedVariantSku?.Attributes.SKUReelle.Value || productSKUReelle.Value;

    return { ...product.Attributes, ...variantSkus?.[targetSku]?.Attributes };
  }

  function getVariantsAttributes(
    product: ProductType,
    variants: ProductVariantsColorsType
  ): ProductVariantsSkusType | undefined {
    return variants[selectedColor ?? product.Attributes.couleur.Value];
  }

  function formattedColors(
    variants: ProductVariantsColorsType
  ): ProductColorsType[] {
    return Object.keys(variants).map(key => {
      const colorVariants = variants[key];
      const variant = colorVariants[Object.keys(colorVariants)[0]];

      return {
        couleur: key,
        couleurStandard: variant.Attributes.couleurStandard.Value,
        pastille: variant.Attributes.imgPastillePivot.Value,
        url: LSModeEnabled ? getLSProductUri(variant.Url) : variant.Url,
      };
    });
  }

  function getProductType(sizes: string[], cups: string[]): ProductKind {
    let type = 0;

    switch (true) {
      case cups.length > 0:
        type = 2;
        break;
      case sizes.length >= 1 && !uniqSizeLabel.includes(sizes[0]):
        type = 1;
        break;
    }

    return type as ProductKind;
  }

  return useMemo(() => {
    if (!data) return undefined;

    // Handle large size mode and retrieve original data if large size mode is not possible
    const {
      breadcrumb,
      cups,
      product,
      sizes,
      variants,
      variantsSkus,
    } = LSModeEnabled ? filterLargeSizeData(data) : data;

    return {
      breadcrumb: breadcrumb, // This data comes from the original data, do not change, can be used for initialData!
      product: product, // This data comes from the original data, do not change, can be used for initialData!
      productAttributes: formatProductAttributes(product, variants),
      variants: variants, // This data comes from the original data, do not change, can be used for initialData!
      variantsAttributes: getVariantsAttributes(product, variants),
      colors: formattedColors(variants),
      sizes: sizes, // This data comes from the original data, do not change, can be used for initialData!
      cups: cups, // This data comes from the original data, do not change, can be used for initialData!
      variantsSkus: variantsSkus, // This data comes from the original data, do not change, can be used for initialData!
      productType: getProductType(sizes, cups),
    };
  }, [data, selectedColor, forceProductSku, LSModeEnabled]);
}
